import React, { useState, useEffect, useRef } from 'react';
import { ArrowLeft, Mail } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';

const API_BASE = import.meta.env.VITE_API_BASE;

export default function UpdateEmail({ onEmailUpdated }) {
  const navigate = useNavigate();
  const { regToken } = useParams();
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(0);
  const [email, setEmail] = useState('');
  const initialCheckDone = useRef(false);

  // Check if registration is still valid
  useEffect(() => {
    if (initialCheckDone.current) return;
    initialCheckDone.current = true;

    const checkRegistration = async () => {
      try {
        const res = await fetch(`${API_BASE}/signup/${regToken}/mailv`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (res.status === 404) {
          // Registration not found, redirect to signup
          sessionStorage.removeItem('registrationToken');
          navigate('/signup', { replace: true });
          return;
        }

        const data = await res.json();

        // Handle redirect URL if present
        if (data.redirectUrl) {
          if (data.redirectUrl === '/login') {
            sessionStorage.removeItem('registrationToken');
          }
          navigate(data.redirectUrl, { replace: true });
          return;
        }

        if (!res.ok) {
          setGeneralError(data.message || 'Failed to verify registration status');
        }
      } catch (err) {
        console.error('Failed to check registration:', err);
        setGeneralError('Unable to connect to the server');
      }
    };

    checkRegistration();
  }, [regToken, navigate]);

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    setErrors({});
    setGeneralError('');
  };

  const validate = () => {
    const newErrors = {};
    let isValid = true;

    if (!email) {
      newErrors.Email = 'Email is required';
      isValid = false;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      newErrors.Email = 'Invalid email address';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading || !validate() || resendCooldown > 0) return;

    setLoading(true);
    setErrors({});
    setGeneralError('');

    try {
      const res = await fetch(`${API_BASE}/signup/${regToken}/email`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          Email: email
        })
      });

      const data = await res.json().catch(() => ({}));

      if (res.ok) {
        // Start the 15-second cooldown
        setResendCooldown(15);
        const interval = setInterval(() => {
          setResendCooldown(prev => {
            if (prev <= 1) {
              clearInterval(interval);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);

        // Navigate back to the registration page which will show the verify email component
        navigate(`/signup/${regToken}`, { replace: true });
        return;
      }
      
      if (res.status === 400 && data.errors) {
        setErrors(data.errors);
      } else if (res.status === 429) {
        setGeneralError('Please wait a few more seconds before updating your email address again.');
      } else {
        setGeneralError(data.message || 'Failed to update email address');
      }
    } catch (err) {
      console.error('Failed to update email:', err);
      setGeneralError('Unable to connect to the server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="max-w-sm w-full sm:max-w-md p-4 my-8 bg-[var(--card-bg)] rounded-lg border border-[var(--border-color)] space-y-6">
        <div className="text-center space-y-4">
          {/* Email Icon */}
          <div className="flex justify-center mb-4">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M20 4H4C2.9 4 2.01 4.9 2.01 6L2 18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM19.6 8.25L12.53 12.67C12.21 12.87 11.79 12.87 11.47 12.67L4.4 8.25C4.15 8.09 4 7.82 4 7.53C4 6.86 4.73 6.46 5.3 6.81L12 11L18.7 6.81C19.27 6.46 20 6.86 20 7.53C20 7.82 19.85 8.09 19.6 8.25Z" fill="var(--accent-primary)"/>
            </svg>
          </div>
          
          <h2 className="text-2xl font-bold text-[var(--text-primary)]">Update Email</h2>
        </div>

        {generalError && (
          <div className="p-4 rounded bg-[var(--card-bg)] border border-[var(--border-color)] shadow-sm">
            <div className="flex items-center gap-3">
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M10 0C4.48 0 0 4.48 0 10C0 15.52 4.48 20 10 20C15.52 20 20 15.52 20 10C20 4.48 15.52 0 10 0ZM11 15H9V13H11V15ZM11 11H9V5H11V11Z" fill="var(--accent-primary)"/>
              </svg>
              <span className="text-[var(--text-primary)] text-sm">{generalError}</span>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <div className="relative">
              <input
                type="email"
                name="new-email"
                id="email"
                value={email}
                onChange={handleEmailChange}
                disabled={loading}
                autoComplete="new-email"
                autoCapitalize="off"
                autoCorrect="off"
                spellCheck="false"
                data-form-type="other"
                className={`w-full p-2 pl-10 pr-9 h-10 border rounded bg-[var(--card-bg)] focus:outline-none ${
                  errors.Email 
                    ? 'border-red-500 focus:ring-2 focus:ring-red-500' 
                    : 'border-[var(--border-color)] focus:ring-2 focus:ring-[var(--accent-primary)]'
                } disabled:opacity-50`}
                placeholder="Enter your email"
              />
              <Mail className="w-5 h-5 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-secondary)]" />
            </div>
            {errors.Email && (
              <p className="mt-1 text-red-500 text-sm">{errors.Email}</p>
            )}
          </div>

          <div className="space-y-3">
            <button
              type="submit"
              disabled={loading || resendCooldown > 0}
              className="w-full flex justify-center items-center px-4 py-2 font-semibold bg-[var(--accent-primary)] text-[var(--btn-text-on-accent)] rounded hover:bg-[var(--hover-dark)] active:bg-[var(--hover-dark)] focus-visible:bg-[var(--hover-dark)] focus:outline-none focus:ring-2 focus:ring-[var(--accent-primary)] focus:ring-offset-2 focus:ring-offset-[var(--card-bg)] transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-[var(--accent-primary)]"
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="w-5 h-5 border-2 border-[var(--btn-text-on-accent)] border-t-transparent rounded-full animate-spin mr-2"></div>
                  Sending...
                </div>
              ) : resendCooldown > 0 ? (
                'Verify Email'
              ) : (
                'Verify Email'
              )}
            </button>
          </div>
        </form>

        <div className="flex justify-start">
          <button
            onClick={() => navigate(`/signup/${regToken}`)}
            className="flex items-center text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors duration-200"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Return
          </button>
        </div>
      </div>
    </div>
  );
} 